import { useEffect, useState } from "react";
import { Page, Composer, MessageList, Skeleton } from "../components/ui.jsx";
import { readList, appendItem } from "../lib/storage.js";
import { rankOf } from "../data/levels.js";

// Public prayer/testimony wall, plus the private leadership inbox.
// The inbox is also gated server-side (READ_RULES in storage.mts) — the check
// here only decides whether to render it.
export default function Wall({ back, userName, userRank }) {
  const [wall, setWall] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [kind, setKind] = useState("prayer");
  const [isPublic, setIsPublic] = useState(true);
  const [sentPrivately, setSentPrivately] = useState(false);
  const [loading, setLoading] = useState(true);

  const isDirector = userRank >= rankOf("director");

  useEffect(() => {
    Promise.all([readList("wall"), readList("leadership-inbox")]).then(([w, i]) => {
      setWall(w);
      setInbox(i);
      setLoading(false);
    });
  }, []);

  const post = async (text) => {
    const item = { name: userName, text, ts: Date.now(), kind };
    if (isPublic) {
      setWall(await appendItem("wall", item));
    } else {
      await appendItem("leadership-inbox", { ...item, private: true });
      setSentPrivately(true);
    }
  };

  return (
    <Page
      back={back}
      eyebrow="The Wall"
      title="Prayer & testimony."
      intro="Post a prayer request or share what God did. Public posts land on the wall for the whole Tribe; private ones go straight to senior leadership."
    >
      <Composer
        placeholder={
          kind === "prayer" ? "What can we stand with you for?" : "Tell us what God did…"
        }
        busyLabel={isPublic ? "Post to the wall" : "Send privately"}
        onPost={post}
      >
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div className="seg">
            <button
              className={kind === "prayer" ? "on" : ""}
              onClick={() => setKind("prayer")}
            >
              🙏 Prayer
            </button>
            <button
              className={kind === "testimony" ? "on" : ""}
              onClick={() => setKind("testimony")}
            >
              🔥 Testimony
            </button>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            Share on the public wall
          </label>
        </div>
      </Composer>

      {sentPrivately && (
        <div className="empty-note">✓ Sent privately to leadership. You're covered.</div>
      )}

      <div className="privacy-note">
        Private notes are routed to Director-level and above. Preview note: this hub is a
        prototype — for deeply sensitive conversations, reach a pastor directly rather
        than through the app.
      </div>

      <div className="block" style={{ marginTop: 24 }}>
        <h3>The wall</h3>
      </div>
      {loading ? (
        <Skeleton />
      ) : (
        <MessageList
          items={wall}
          kindTag
          empty="The wall is waiting. Post the first prayer or testimony."
        />
      )}

      {isDirector && (
        <>
          <div className="block" style={{ marginTop: 28 }}>
            <h3>🔒 Leadership inbox</h3>
            <p>
              Private submissions — visible to Directors, Executive Staff, and Senior
              only. Follow up personally and honor discretion (Code 13).
            </p>
          </div>
          {loading ? (
            <Skeleton />
          ) : (
            <MessageList items={inbox} kindTag empty="No private submissions yet." />
          )}
        </>
      )}
    </Page>
  );
}
